import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import uploadGif from "../img/product/labeling/upload1.webm";
import createEventGif from "../img/product/labeling/create-event1.webm";
import rulesGif from "../img/product/labeling/rules1.webm";
import chatEventsDetectionGif from "../img/product/labeling/chat_events_detection1.webm";
import chatAverageValueGif from "../img/product/labeling/chat_average_value1.webm";
import chatSimilarValuesGif from "../img/product/labeling/chat_similar_values1.webm";
import chatYesterdayValueGif from "../img/product/labeling/chat_yesterday_value1.webm";
import pdfReportGif from "../img/product/labeling/pdf_report1.webm";
import exportDataGif from "../img/product/labeling/export_data1.webm";
import "../styles/labeling.scss";
import { getCanonicalUrl } from "../utils/seoConfig";

function Labeling() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const featureRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Intersection Observer za animacije
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
        }
      });
    }, observerOptions);

    const currentRefs = featureRefs.current;
    currentRefs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      currentRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  const features = [
    {
      id: "upload",
      video: uploadGif,
      title: t("LabelingUploadTitle"),
      description: t("LabelingUploadDesc"),
      tag: t("DataImport"),
      reverse: false,
    },
    {
      id: "create-event",
      video: createEventGif,
      title: t("LabelingCreateEventTitle"),
      description: t("LabelingCreateEventDesc"),
      tag: t("EventLabeling"),
      reverse: true,
    },
    {
      id: "rules",
      video: rulesGif,
      title: t("LabelingRulesTitle"),
      description: t("LabelingRulesDesc"),
      tag: t("AutomatedLabeling"),
      reverse: false,
    },
    {
      id: "chat-events",
      video: chatEventsDetectionGif,
      title: t("LabelingChatEventsTitle"),
      description: t("LabelingChatEventsDesc"),
      tag: t("AIAssistant"),
      reverse: true,
    },
    {
      id: "chat-average",
      video: chatAverageValueGif,
      title: t("LabelingChatAverageTitle"),
      description: t("LabelingChatAverageDesc"),
      tag: t("AIAssistant"),
      reverse: false,
    },
    {
      id: "chat-similar",
      video: chatSimilarValuesGif,
      title: t("LabelingChatSimilarTitle"),
      description: t("LabelingChatSimilarDesc"),
      tag: t("AIAssistant"),
      reverse: true,
    },
    {
      id: "chat-yesterday",
      video: chatYesterdayValueGif,
      title: t("LabelingChatYesterdayTitle"),
      description: t("LabelingChatYesterdayDesc"),
      tag: t("AIAssistant"),
      reverse: false,
    },
    {
      id: "pdf-report",
      video: pdfReportGif,
      title: t("LabelingPdfReportTitle"),
      description: t("LabelingPdfReportDesc"),
      tag: t("Reporting"),
      reverse: true,
    },
    {
      id: "export-data",
      video: exportDataGif,
      title: t("LabelingExportDataTitle"),
      description: t("LabelingExportDataDesc"),
      tag: t("DataExport"),
      reverse: false,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t("LabelingPageTitle")}</title>
        <link rel="canonical" href={getCanonicalUrl("/products/labeling")} />
      </Helmet>
      <div className="labeling-page">
        <div className="labeling-header">
          <div className="labeling-header-content">
            <span className="product-tag">{t("B2CAndB2B")}</span>
            <h1 className="labeling-header-title">{t("TimeSeriesLabeling")}</h1>
            <p className="labeling-header-description">
              {t("TimeSeriesLabelingDescription")}
            </p>
            <div className="product-features">
              <div className="product-feature">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 13L9 17L19 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{t("CSVImportExport")}</span>
              </div>
              <div className="product-feature">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 13L9 17L19 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{t("EventAnnotation")}</span>
              </div>
              <div className="product-feature">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 13L9 17L19 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{t("Collaboration")}</span>
              </div>
            </div>
            <div className="product-cta">
              <a
                href="https://labeling.predsense.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="primary-button"
              >
                {t("StartFree")}
              </a>
              <button
                className="secondary-button"
                onClick={() => navigate("/pricing")}
              >
                {t("ViewPricing")}
              </button>
            </div>
          </div>
        </div>

        <div className="labeling-features">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              ref={(el) => (featureRefs.current[index] = el)}
              className={`labeling-feature-item ${
                feature.reverse ? "reverse" : ""
              }`}
            >
              <div className="labeling-feature-content">
                <span className="labeling-feature-tag">{feature.tag}</span>
                <h2 className="labeling-feature-title">{feature.title}</h2>
                <p className="labeling-feature-description">
                  {feature.description}
                </p>
              </div>
              <div className="labeling-feature-video-wrapper">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="labeling-feature-video"
                  aria-label={feature.title}
                >
                  <source src={feature.video} type="video/webm" />
                </video>
                <div className="labeling-video-overlay"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default Labeling;
